'use client';
import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';

type Language = 'EN' | 'ZH';

const STORAGE_KEY = 'reset_running_language';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  setLanguage: (lang: Language) => void;
  t: (en: string, zh: string) => string;
  updateUserLanguagePreference: (lang: Language) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'EN',
  toggleLanguage: () => {},
  setLanguage: () => {},
  t: (en) => en,
  updateUserLanguagePreference: async () => {},
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>('EN');
  const [mounted, setMounted] = useState(false);
  // Prevent repeated Supabase calls
  const loadedRef = useRef(false);

  // On mount: priority = Supabase profile → localStorage → default EN
  // Runs ONCE only — loadedRef prevents re-runs
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    let cancelled = false;

    async function loadLanguage() {
      // 1. Try localStorage first (fast, synchronous)
      let saved: Language | null = null;
      try {
        const raw = localStorage.getItem(STORAGE_KEY) as Language | null;
        if (raw === 'EN' || raw === 'ZH') saved = raw;
      } catch {
        // ignore
      }

      if (saved && !cancelled) setLanguageState(saved);

      // 2. Try Supabase user profile (async, overrides localStorage if found)
      try {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        const { data: { user } } = await supabase.auth.getUser();
        if (user && !cancelled) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('preferred_language')
            .eq('id', user.id)
            .maybeSingle();
          if (profile?.preferred_language && (profile.preferred_language === 'EN' || profile.preferred_language === 'ZH')) {
            if (!cancelled) {
              setLanguageState(profile.preferred_language as Language);
              try { localStorage.setItem(STORAGE_KEY, profile.preferred_language); } catch {}
            }
          }
        }
      } catch {
        // Supabase not available or not logged in — keep localStorage value
      }

      if (!cancelled) setMounted(true);
    }

    loadLanguage();
    return () => { cancelled = true; };
  }, []); // run once on mount only

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // ignore
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'EN' ? 'ZH' : 'EN');
  };

  // Sync language preference to Supabase user profile when logged in
  const updateUserLanguagePreference = async (lang: Language) => {
    setLanguage(lang);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('user_profiles')
          .update({ preferred_language: lang })
          .eq('id', user.id);
      }
    } catch {
      // ignore — localStorage already updated
    }
  };

  const t = (en: string, zh: string) => (language === 'EN' ? en : zh);

  return (
    <LanguageContext.Provider
      value={{
        language: mounted ? language : 'EN',
        toggleLanguage,
        setLanguage,
        t,
        updateUserLanguagePreference,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}